import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { v2Api } from '@/lib/api';
import { ManageInjectsContent } from '@/components/scenarios/ManageInjectsContent';
import { AllInjectsContent } from '@/components/scenarios/AllInjectsContent';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Loader2, ListChecks, ArrowLeft } from 'lucide-react';
import type { ScenarioItem } from '@/components/scenarios/ScenarioList';

const ALL_INJECTS_VALUE = '__all__';

export default function InjectsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scenarioParam = searchParams.get('scenario') ?? '';
  const [scenarios, setScenarios] = useState<ScenarioItem[]>([]);
  const [loadingScenarios, setLoadingScenarios] = useState(true);
  const [selectedScenarioId, setSelectedScenarioId] = useState(
    scenarioParam || ALL_INJECTS_VALUE
  );

  useEffect(() => {
    setSelectedScenarioId(scenarioParam || ALL_INJECTS_VALUE);
  }, [scenarioParam]);

  const fetchScenarios = useCallback(async () => {
    setLoadingScenarios(true);
    try {
      const paged = await v2Api.dashboard.scenarios.list<ScenarioItem>({});
      setScenarios(paged.results ?? []);
    } catch {
      setScenarios([]);
    } finally {
      setLoadingScenarios(false);
    }
  }, []);

  useEffect(() => {
    fetchScenarios();
  }, [fetchScenarios]);

  const ttxScenarios = scenarios.filter((s) => s.ttx_enabled);
  const addMode = searchParams.get('add') === '1';
  const editId = searchParams.get('edit');
  const formOpen = addMode || !!editId;

  const handleBackToList = () => {
    if (selectedScenarioId) {
      setSearchParams({ scenario: selectedScenarioId });
    } else {
      setSearchParams({});
    }
  };

  const handleScenarioChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value;
    setSelectedScenarioId(id);
    if (id && id !== ALL_INJECTS_VALUE) {
      setSearchParams({ scenario: id });
    } else {
      setSearchParams({});
    }
  };

  if (loadingScenarios) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <ListChecks className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                Manage Injects
              </h1>
              <p className="text-muted-foreground mt-1">
                View all injects with filters and sorting, or select a scenario to add, edit, or delete injects.
              </p>
            </div>
          </div>
          {formOpen && selectedScenarioId && selectedScenarioId !== ALL_INJECTS_VALUE && (
            <Button variant="ghost" size="sm" onClick={handleBackToList}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to list
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-2 max-w-md">
        <Label htmlFor="injects-scenario">View</Label>
        <select
          id="injects-scenario"
          className="flex h-10 w-full rounded-md border border-input bg-background text-foreground px-3 py-2 text-sm shadow-sm"
          value={selectedScenarioId}
          onChange={handleScenarioChange}
        >
          <option value={ALL_INJECTS_VALUE}>All injects</option>
          {ttxScenarios.length === 0 ? (
            <option value="" disabled>
              No scenarios with TTX enabled
            </option>
          ) : (
            ttxScenarios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))
          )}
        </select>
        {scenarios.length > 0 && ttxScenarios.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Enable TTX on a scenario from its detail page first.
          </p>
        )}
      </div>

      {selectedScenarioId === ALL_INJECTS_VALUE ? (
        <AllInjectsContent ttxScenarios={ttxScenarios.map((s) => ({ id: s.id, name: s.name }))} />
      ) : selectedScenarioId ? (
        <ManageInjectsContent scenarioId={selectedScenarioId} />
      ) : (
        <div className="rounded-lg border border-dashed p-8 text-center text-muted-foreground">
          Select a scenario above to add, edit, or delete injects.
        </div>
      )}
    </div>
  );
}
